import { useEffect, useRef, useState } from "react";
import "./AutoTransactTable.scss";
import orderEmptyImg from "../../../assets/images/empty_order_pipeline.svg";
import EmptyOrder from "../../EmptyOrder";
import Loader from "../../Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchSkuConfigData } from "../../../store/slices/CTDBSkuConfigSlice";
import {
  NO_TRANSACTIONS_HEADING,
  NO_TRANSACTIONS_TEXT,
} from "../../../Constant";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import { containsEmoji } from "../../../utils/common";

export interface AutoTransactColumnConfig {
  key: string;
  label: any;
  render?: (row: any) => React.ReactNode;
}

interface AutoTransactTableProps {
  data: any[];
  columnsData: AutoTransactColumnConfig[];
  setOrdersAutoTransact: React.Dispatch<React.SetStateAction<any[]>>;
  setShowPagination: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string;
  isLoading: boolean;
  SkuName: string;
  TransactionStatus: string;
  ProductName: string;
}

const AutoTransactTable: React.FC<AutoTransactTableProps> = ({
  data,
  columnsData,
  setShowPagination,
  searchText,
  isLoading,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const configData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );
  const tableRef = useRef<HTMLTableElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [fixedHeader, setFixedHeader] = useState(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current) return;
      const tableTop = tableRef.current.getBoundingClientRect().top;
      setFixedHeader(tableTop < 0);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!tableRef.current) return;
    const firstRow = tableRef.current.querySelector("tr");
    if (!firstRow) return;
    const widths: number[] = Array.from(firstRow.children).map(
      (cell: any) => cell.getBoundingClientRect().width,
    );
    setColumnWidths(widths);
  }, [columnsData]);

  const syncScroll = () => {
    if (tableScrollRef.current) {
      const scrollLeft = tableScrollRef.current.scrollLeft;
      const fixedHeaderTable =
        document.querySelector<HTMLTableElement>(".fixed-header");
      if (fixedHeaderTable) {
        fixedHeaderTable.style.transform = `translateX(-${scrollLeft}px)`;
      }
    }
  };

  useEffect(() => {
    // Only show pagination if there is data
    if (Array.isArray(data) && data.length > 0) {
      setShowPagination(true);
    } else {
      setShowPagination(false);
    }
  }, [data, setShowPagination]);

  if (isLoading) {
    return <Loader />;
  }

  const renderNoSearchResult = () => {
    return <NoSearchResult searchValue={searchText} />;
  };

  if (Array.isArray(data) && data?.length === 0) {
    const hasEmoji = containsEmoji(searchText);
    if (!searchText || hasEmoji) {
      return (
        <EmptyOrder
          image={orderEmptyImg}
          header={NO_TRANSACTIONS_HEADING}
          subHeader={NO_TRANSACTIONS_TEXT}
        />
      );
    }
    return renderNoSearchResult();
  } else {
    if (configData === null && data && data.length > 0) {
      dispatch(fetchSkuConfigData());
    }

    if (!data) {
      return <Loader />;
    }

    const getClassNamesAutoTransactTable = (key: string) => {
      switch (key) {
        case "SkuId":
          return "auto-transact-sku-id";
        case "ProductName":
          return "transaction-product-name";
        case "TransactionId":
          return "auto-transact-transaction-ID";
        case "UsagePeriod":
          return "auto-transact-usage-period";
        case "EngagementName":
          return "auto-transact-engagement-name";
        case "CostMetric":
          return "auto-transact-cost-metric";
        case "UnitCost":
          return "auto-transact-unit-cost";
        case "Quantity":
          return "auto-transact-quantity";
        default:
          return "auto-transact-total text-right-align";
      }
    };

    return (
      <div
        className="autotransact-table-container"
        style={{ position: "relative" }}
      >
        {fixedHeader && (
          <div
            className="fixed-header-wrapper"
            style={{
              overflow: "hidden",
              width: tableScrollRef.current?.getBoundingClientRect().width,
            }}
          >
            <table
              className="motif-table-standard fixed-header"
              style={{ width: columnWidths.reduce((a, b) => a + b, 0) }}
            >
              <thead>
                <tr>
                  {columnsData.map((col, index) => (
                    <th
                      key={col.key}
                      className={getClassNamesAutoTransactTable(col.key)}
                      style={{ width: columnWidths[index] }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
        )}
        <div
          className="table-scroll-wrapper"
          ref={tableScrollRef}
          style={{ overflowX: "auto" }}
          onScroll={syncScroll}
        >
          <table className="motif-table-standard" ref={tableRef}>
            <thead>
              <tr>
                {columnsData.map((col) => (
                  <th
                    key={col.key}
                    className={getClassNamesAutoTransactTable(col.key)}
                    style={{ minWidth: "160px" }}
                  >
                    {col.label}{" "}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.map((order) => {
                return (
                  <tr>
                    {columnsData.map((col) => (
                      <td key={col.key}>
                        {col.render ? col.render(order) : order[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
};

export default AutoTransactTable;
